import { DirectionDto } from 'api/generated';
import { useMemo } from 'react';
import { selectors, useAppSelector } from 'store';
import { Theme } from 'styles/themes';
import { appDefaultColors } from 'styles/themes/appDefaultColors';

/**
 * Custom theme hook for CreateUser page that allows color override via URL parameter
 *
 * @param direction - The direction object from the store
 * @param overrideColor - Optional color to override the direction's main color (from URL parameter)
 * @returns Theme instance with the appropriate primary color
 */
const useCreateUserTheme = (direction: DirectionDto | null, overrideColor?: string | null) => {
  const isDarkMode = useAppSelector(selectors.settings.isDarkMode);

  const primaryColor = useMemo(() => {
    // Validate override color format (hex color with #)
    if (overrideColor && /^#([AaBbCcDdEeFf0-9]{6})$/.test(overrideColor)) {
      return overrideColor;
    }

    // If override color is provided but invalid format, try adding # prefix
    if (overrideColor && /^([AaBbCcDdEeFf0-9]{6})$/.test(overrideColor)) {
      return `#${overrideColor}`;
    }

    // Fallback to direction color or default
    return direction?.mainColorHex || '#DF6438';
  }, [overrideColor, direction?.mainColorHex]);

  const theme = useMemo(
    () =>
      new Theme(primaryColor, isDarkMode ? appDefaultColors.dark : appDefaultColors.default, {
        old: direction?.oldColorHex || '',
      }),
    [primaryColor, isDarkMode, direction?.oldColorHex],
  );

  return theme;
};

export default useCreateUserTheme;
